import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
  TextField,
} from "@mui/material";
import { TableSortLabel } from "@mui/material";
import {
  getComparator,
  stableSort,
} from "../../../../common-utilities/sortingUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import {
  GetAllCoolerShippings,
  DeleteCoolerShipping,
} from "../../../services/CoolerShippingService";
import ViewCoolerShipping from "./ViewCoolerShipping";
import CommonLoader from "../../common/CommonLoader";
import { useNavigate } from "react-router-dom";

const CoolerShippingsTable = () => {
  const columns = [
    { id: "id", label: "ID" },
    { id: "coolerId", label: "Cooler ID" },
    { id: "carrier", label: "Carrier" },
    { id: "trackingNumber", label: "Tracking #" },
    { id: "labelId", label: "Label ID" },
    { id: "clientAccountNumber", label: "Client #" },
    { id: "createdDate", label: "Created Date" },
    { id: "actions", label: "Actions" },
  ];

  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [shippingData, setShippingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [searchText, setSearchText] = useState("");
  const [tableHeight, setTableHeight] = useState(600);

  const tableContainerRef = useRef(null);
  const navigate = useNavigate();

  // Dynamic table height calculation
  useEffect(() => {
    const calculateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerTop =
          tableContainerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const availableHeight = windowHeight - containerTop - 76;
        const calculatedHeight = Math.max(
          300,
          Math.min(availableHeight, windowHeight - 200)
        );
        setTableHeight(calculatedHeight);
      }
    };

    calculateTableHeight();
    window.addEventListener("resize", calculateTableHeight);
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener("resize", calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [filteredData]);

  useEffect(() => {
    fetchShippings();
  }, [isDeleted]);

  useEffect(() => {
    if (!searchText) {
      setFilteredData(shippingData);
    } else {
      const q = searchText.toLowerCase();
      setFilteredData(
        shippingData.filter(
          (r) =>
            (r.cooler?.coolerId || "").toLowerCase().includes(q) ||
            (r.trackingNumber || "").toLowerCase().includes(q) ||
            (r.carrier || "").toLowerCase().includes(q)
        )
      );
    }
  }, [searchText, shippingData]);

  const fetchShippings = async () => {
    try {
      setLoading(true);
      const response = await GetAllCoolerShippings();
      const data = response?.results || [];
      setShippingData(data);
      setFilteredData(data);
    } catch (error) {
      console.error("Error fetching cooler shippings:", error);
      NotificationManager.error("Error fetching cooler shippings");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (shipping) => setSelectedShipping(shipping);

  const handleBackClick = () => setSelectedShipping(null);

  const handleDeleteClick = (shipping) => {
    setSelectedShipping(shipping);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedShipping(null);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      const response = await DeleteCoolerShipping(selectedShipping.id);
      if (response.results === true) {
        NotificationManager.success(
          "Shipping record deleted successfully",
          "Success"
        );
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error deleting shipping record", "Error");
      }
    } catch (error) {
      NotificationManager.error("Error deleting shipping record", "Error");
    } finally {
      setLoading(false);
      setSelectedShipping(null);
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleEditClick = (shipping) =>
    navigate(`/cooler-shippings/${shipping.id}`);

  const renderCell = (col, shipping) => {
    if (col.id === "coolerId")
      return shipping.cooler?.coolerId || shipping.coolerId || "N/A";
    if (col.id === "createdDate")
      return shipping.createdDate
        ? new Date(shipping.createdDate).toLocaleString([], {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
        : "N/A";
    return shipping[col.id] || "N/A";
  };

  const renderActions = (shipping) => (
    <div style={{ minWidth: "100px", display: "flex" }}>
      <Tooltip title="Edit Shipping">
        <IconButton
          style={{ color: "orange" }}
          onClick={() => handleEditClick(shipping)}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="View Shipping">
        <IconButton
          style={{ color: "green" }}
          onClick={() => handleViewClick(shipping)}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Delete Shipping">
        <IconButton
          style={{ color: "red" }}
          onClick={() => handleDeleteClick(shipping)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <>
      <span className="title">Cooler Shippings</span>
      <hr />

      {selectedShipping ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewCoolerShipping shipping={selectedShipping} />
        </>
      ) : (
        <>
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >

            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: 1, maxWidth: "400px" }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/cooler-shippings/add")}
            >
              Add Shipping
            </Button>
          </div>

          <Paper>
            <TableContainer
              ref={tableContainerRef}
              sx={{ maxHeight: tableHeight, overflowY: "auto", width: "99%" }}
            >
              <Table size="small" stickyHeader>
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.id}>
                        {col.id === "actions" ? (
                          col.label
                        ) : (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleRequestSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stableSort(filteredData, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((shipping, index) => (
                      <TableRow
                        key={shipping.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0,0,0,0.04)" : "white",
                        }}
                      >
                        {columns.map((col) => (
                          <TableCell key={col.id}>
                            {col.id === "actions"
                              ? renderActions(shipping)
                              : renderCell(col, shipping)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        message="Are you sure you want to delete this shipping record?"
      />

      <CommonLoader loading={loading} />
    </>
  );
};

export default CoolerShippingsTable;